import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';

import { Model } from 'survey-core';
import { SurveyModule } from 'survey-angular-ui';

import { FormResponseService } from '../../services';
import { FormStatus } from '../../enums';
import { ErrorMessageService, ToastMessageService, ImageUploadService } from '@fitness-force/api';
import { OwnershipLevel } from '@fitness-force/enum';
import {
  Subject,
  Subscription,
  debounceTime,
  firstValueFrom,
  take,
  takeUntil,
} from 'rxjs';

@Component({
  selector: 'lib-staff-form-fill',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonModule, SurveyModule],
  templateUrl: './staff-form-fill.component.html',
  styleUrls: ['./staff-form-fill.component.scss'],
})
export class StaffFormFillComponent implements OnInit, OnDestroy {
  private readonly responseService = inject(FormResponseService);
  private readonly toastService = inject(ToastMessageService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly imageUploadService = inject(ImageUploadService);

  tenantId = input.required<string>();
  responseId = input<string>('');
  responseStatus = input<FormStatus | string>('');
  templateId = input<string>('');
  clientId = input<string>('');
  templateSchema = input.required<any>();
  currentData = input<any>();
  readonly = input<boolean>(false);
  ownershipLevel = input<OwnershipLevel>(OwnershipLevel.Tenant);
  franchiseId = input<number>(0);

  /** Emitted when form is completed successfully (API succeeded) */
  formCompleted = output<void>();
  /** Emitted when form submit/complete fails - parent should not reload data on close */
  formError = output<void>();

  surveyModel!: Model;

  loading = signal(false);
  completed = signal(false);
  savingDraft = signal(false);
  completing = signal(false);

  private readonly valueChange$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private valueChangeSub?: Subscription;

  ngOnInit(): void {
    this.initializeSurvey();
    this.setupAutoSave();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.valueChangeSub?.unsubscribe();
    this.surveyModel?.dispose?.();
  }

  private initializeSurvey(): void {
    const schema = this.templateSchema();
    const data = this.currentData();
    const isReadonly = this.readonly();

    this.surveyModel = new Model(schema);

    if (data) {
      this.surveyModel.data = data;
    }

    if (isReadonly) {
      this.surveyModel.mode = 'display';
      this.surveyModel.showCompleteButton = false;
    } else {
      this.surveyModel.onValueChanged.add(() => {
        this.valueChange$.next();
      });

      this.surveyModel.onCurrentPageChanged.add(() => {
        this.saveDraft();
      });

      this.surveyModel.onCompleting.add((sender, options) => {
        return this.handleCompleting(sender.data, options);
      });
    }

    // Handle image uploads (works in both readonly and edit mode)
    // Try onUploadFiles first (newer versions), fallback to onUploadFile
    if (this.surveyModel.onUploadFiles) {
      this.surveyModel.onUploadFiles.add((_survey: any, options: any) => {
        this.handleImageUpload(options);
      });
    } else if ((this.surveyModel as any).onUploadFile) {
      (this.surveyModel as any).onUploadFile.add((_survey: any, options: any) => {
        this.handleImageUpload(options);
      });
    }
  }

  private setupAutoSave(): void {
    if (!this.responseId()) return;
    this.valueChangeSub = this.valueChange$
      .pipe(debounceTime(1500), takeUntil(this.destroy$))
      .subscribe(() => this.saveDraft());
  }

  saveDraft(): void {
    if (this.savingDraft() || this.completed()) return;

    const tenantId = this.tenantId();
    const responseId = this.responseId();
    const responseStatus = this.responseStatus();
    if (!tenantId || !responseId) return;

    this.savingDraft.set(true);
    const surveyData = this.surveyModel.data;

    let updateObservable;
    if (
      responseStatus === FormStatus.DRAFT ||
      responseStatus === 'DRAFT' ||
      responseStatus === 'Draft' ||
      responseStatus === 'draft' ||
      responseStatus === FormStatus.PENDING ||
      responseStatus === 'PENDING' ||
      responseStatus === 'Pending' ||
      responseStatus === 'pending'
    ) {
      updateObservable = this.responseService.updateResponseDraft(tenantId, responseId, {
        responseJson: JSON.stringify(surveyData ?? {})
      });
    } else {
      updateObservable = this.responseService.updateResponse(tenantId, responseId, {
        response_json: JSON.stringify(surveyData ?? {})
      });
    }

    updateObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.savingDraft.set(false);
        },
        error: (error) => {
          this.savingDraft.set(false);
          this.errorMessageService
            .returnErrorMessage(error)
            .pipe(take(1))
            .subscribe((msg) => this.toastService.showErrorMessage(msg));
        },
      });
  }

  private async handleCompleting(
    surveyData: unknown,
    options: { allow: boolean; message?: string }
  ): Promise<void> {
    if (this.completing() || this.completed()) return;

    const responseId = this.responseId();
    const templateId = this.templateId();
    const clientId = this.clientId();
    const tenantId = this.tenantId();
    const responseStatus = this.responseStatus();

    this.completing.set(true);

    try {
      if (responseId) {
        if (
          responseStatus === FormStatus.DRAFT ||
          responseStatus === 'DRAFT' ||
          responseStatus === 'Draft' ||
          responseStatus === 'draft' ||
          responseStatus === FormStatus.PENDING ||
          responseStatus === 'PENDING' ||
          responseStatus === 'Pending' ||
          responseStatus === 'pending'
        ) {
          await firstValueFrom(
            this.responseService.updateResponseComplete(tenantId, responseId, {
              responseJson: JSON.stringify(surveyData ?? {}),
            })
          );
        } else {
          await firstValueFrom(
            this.responseService.updateResponse(tenantId, responseId, {
              status: FormStatus.COMPLETED,
              response_json: JSON.stringify(surveyData ?? {}),
            })
          );
        }
      } else if (templateId && clientId) {
        await firstValueFrom(
          this.responseService.createResponse(tenantId, {
            template_id: parseInt(templateId, 10),
            client_id: clientId,
            status: FormStatus.COMPLETED,
            expiry_date:new Date(new Date().setDate(new Date().getDate() + 30)).toISOString(),
            response_json: JSON.stringify(surveyData ?? {}),
          })
        );
      } else {
        options.allow = false;
        options.message = 'Missing template or client information';
        this.completing.set(false);
        return;
      }

      this.completed.set(true);
      this.completing.set(false);
      this.toastService.showSuccess('Form completed successfully');
      this.formCompleted.emit();
    } catch (error) {
      options.allow = false;
      this.completing.set(false);
      this.formError.emit();
      const msg = await firstValueFrom(
        this.errorMessageService.returnErrorMessage(error).pipe(take(1))
      );
      options.message = msg;
      this.toastService.showErrorMessage(msg);
    }
  }

  private handleImageUpload(options: any): void {
    // Get files from options
    const files: File[] = options.files;
    if (!files || files.length === 0) {
      if (options.callback) {
        options.callback('error');
      }
      this.toastService.showErrorMessage('No files to upload');
      return;
    }

    // Process the first file (SurveyJS typically handles one at a time)
    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      if (options.callback) {
        options.callback('error');
      }
      this.toastService.showErrorMessage('Please select a valid image file');
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const base64String = reader.result as string;
      // Remove the data:image/xxx;base64, prefix
      const base64WithoutPrefix = base64String.replace(/^data:image\/[a-z]+;base64,/, '');

      const payload = {
        image_base64: base64WithoutPrefix,
        file_name: file.name,
      };

      const tenantId = parseInt(this.tenantId(), 10);
      const franchiseId = this.franchiseId();
      const ownership = this.ownershipLevel();

      this.imageUploadService
        .UploadImagesByOwnership(ownership, payload, tenantId, franchiseId)
        .pipe(take(1))
        .subscribe({
          next: (res: any) => {
            if (res.image_path) {
              // Call the callback with 'success' and the image URL
              // For onUploadFiles: callback(status, [{ file: file, content: url }])
              // For onUploadFile: callback(status, url)
              if (options.callback) {
                // Try the newer format first (array of objects)
                try {
                  options.callback('success', [{ file: file, content: res.image_path }]);
                } catch {
                  // Fallback to older format (just the URL)
                  options.callback('success', res.image_path);
                }
              }
              this.toastService.showSuccess('Image uploaded successfully');
            } else {
              if (options.callback) {
                options.callback('error');
              }
              this.toastService.showErrorMessage('Failed to upload image');
            }
          },
          error: (error) => {
            if (options.callback) {
              options.callback('error');
            }
            this.errorMessageService
              .returnErrorMessage(error)
              .pipe(take(1))
              .subscribe((msg) => this.toastService.showErrorMessage(msg));
          },
        });
    };

    reader.onerror = () => {
      if (options.callback) {
        options.callback('error');
      }
      this.toastService.showErrorMessage('Failed to read file');
    };

    reader.readAsDataURL(file);
  }
}
